import * as React from "react"
import { connect } from "react-redux"

import { ActionAnchor, ActionType } from "@tenforce/toolbox-button-push-react"
import { Modal, Overlay } from "@tenforce/toolbox-modal-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import { GlobalState } from "../../../store"
import { removeQueryParam, setQueryParam } from "../../../util/Url"

import { DispatchProps, mapDispatchToProps } from "../actions"

import BottomBar from "./BottomBar"
import DictionaryPopup from "./help/dictionary/DictionaryPopup"
import Tooltip from "./help/tooltip/Tooltip"

import ContextualHelpTerm = Models.ContextualHelpTerm
import { Labels } from "../../../util/Translations"

interface StateProps extends ContextualHelp.ContextualHelp {
    helpUrl: string
}

type Props = StateProps & DispatchProps

interface State {
    openedTooltip: Element | null
}

export class ContextualHelpButton extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = {
            openedTooltip: null,
        }
    }

    // the constructor will run when the trigger in the application header will be shown
    // and not when we open the contextual help
    // thus when we open the help, we have to calculate if there is an opened tooltip or not
    static getDerivedStateFromProps(props: Props, _state: State): State {
        const {
            tooltipsOverlay: { selectedTerm },
        } = props

        let openedTooltip = null

        if (selectedTerm && selectedTerm.target) {
            openedTooltip = document.querySelector(selectedTerm.target)
        }

        return {
            openedTooltip,
        }
    }

    render(): JSX.Element {
        const {
            helpUrl,
            isDictionaryPopupVisible,
            isTooltipsOverlayVisible,
            tooltipsOverlay: { supportMailAddress },
        } = this.props

        const { openedTooltip } = this.state
        const { ContextualHelp } = Labels

        const visibleTooltips = this.getVisibleTooltips()

        return (
            <>
                <ActionAnchor
                    id="contextualHelpButton"
                    title={ContextualHelp}
                    actionType={ActionType.icon}
                    extraClasses="is-inverted no-underline"
                    onClick={(): void => this.showTooltipsOverlay()}
                >
                    {Symbols.ContextualHelp}
                </ActionAnchor>

                <Modal
                    isShown={isTooltipsOverlayVisible}
                    containerProps={{
                        id: "contextualHelpModal",
                        willClose: this.onOverlayClicked,
                        onClose: this.hideTooltipsOverlay,
                        isCenterPositioned: true,
                    }}
                    contentProps={{
                        extraClasses: isDictionaryPopupVisible
                            ? "tw-mx-auto tw-my-1/20 tw-w-3/5"
                            : "",
                    }}
                    overlayProps={{
                        isLightColored: true,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore: JSX data-attributes are not supported in nested object types https://github.com/microsoft/TypeScript/issues/28960
                        "data-ft-component": "modal-overlay",
                    }}
                >
                    {isDictionaryPopupVisible ? (
                        <DictionaryPopup />
                    ) : (
                        <>
                            <Overlay
                                extraClasses="tw-z-1"
                                isHidden={!openedTooltip}
                                data-ft-component="tooltip-overlay"
                                isLightColored={true}
                                onClick={(): void =>
                                    this.setTooltip(null, null)
                                }
                            />

                            {visibleTooltips.map((visibleTooltip, index) => {
                                const target = visibleTooltip[0]
                                const dictionarySources = visibleTooltip[1]
                                if (
                                    target === null ||
                                    dictionarySources.length === 0
                                ) {
                                    return <></>
                                }

                                return (
                                    <Tooltip
                                        dictionarySources={dictionarySources}
                                        isContentShown={
                                            openedTooltip === target
                                        }
                                        key={index}
                                        target={target}
                                        toggleTooltip={this.setTooltip}
                                    />
                                )
                            })}

                            <BottomBar
                                helpUrl={helpUrl}
                                showDictionary={(): void => {
                                    this.setTooltip(null, null)
                                    this.showDictionary()
                                }}
                                supportMailAddress={supportMailAddress}
                            />
                        </>
                    )}
                </Modal>
            </>
        )
    }

    onOverlayClicked = (): boolean => {
        const { filterTerms, isDictionaryPopupVisible } = this.props

        if (isDictionaryPopupVisible) {
            this.hideDictionary()
            filterTerms("")
            return false
        }
        return true
    }

    hideDictionary = (): void => {
        const { updateDictionaryPopupVisibility } = this.props
        updateDictionaryPopupVisibility(false)
    }

    showDictionary = (): void => {
        const { updateDictionaryPopupVisibility } = this.props
        updateDictionaryPopupVisibility(true)
    }

    showTooltipsOverlay = (): void => {
        const { updateTooltipsOverlayVisibility } = this.props
        updateTooltipsOverlayVisibility(true)
    }

    hideTooltipsOverlay = (): void => {
        const { updateTooltipsOverlayVisibility } = this.props
        updateTooltipsOverlayVisibility(false)
    }

    setTooltip = (
        element: Element | null,
        term: ContextualHelpTerm | null,
    ): void => {
        const { selectTermInTooltips } = this.props

        if (element === null || term === null) {
            removeQueryParam("tooltip")
            selectTermInTooltips(null)
        } else {
            setQueryParam("tooltip", term.code)
            selectTermInTooltips(term)
        }
    }

    getVisibleTooltips = (): [
        Element | null,
        Models.ContextualHelpDictionary[],
    ][] => {
        const { dictionaries } = this.props

        const visibleTooltips = new Map<
            Element | null,
            Models.ContextualHelpDictionary[]
        >()

        dictionaries.forEach((dictionary) => {
            // separate terms per visible elements
            const visibleTerms = new Map<
                Element | null,
                Models.ContextualHelpTerm[]
            >()

            dictionary.terms
                .filter((term) => term.target !== undefined)
                .forEach((term) => {
                    const target = document.querySelector(term.target as string)
                    if (target) {
                        // make sure that the target is visible and not only part of the DOM
                        // https://developer.mozilla.org/en-US/docs/web/api/htmlelement/offsetparent
                        // offsetParent returns null in the following situations:
                        // - The element or its parent element has the display property set to none.
                        // - The element has the position property set to fixed (Firefox returns <body>).
                        // - The element is <body> or <html>.
                        const style = window.getComputedStyle(target)
                        if (
                            style.position === "fixed" ||
                            !!(target as HTMLElement).offsetParent
                        ) {
                            visibleTerms.set(target, [
                                // add the old terms too
                                ...(visibleTerms.get(target) ?? []),
                                term,
                            ])
                        }
                    }
                })

            // convert the map to an array of [target, term[]]
            Array.from(visibleTerms.entries()).forEach((value) => {
                const target = value[0]
                const terms = value[1]

                // save the whole dictionary source
                // with only a subset of the terms
                // that are relevant for the target
                visibleTooltips.set(target, [
                    // add the old terms too
                    ...(visibleTooltips.get(target) ?? []),
                    {
                        ...dictionary,
                        terms,
                    },
                ])
            })
        })

        // convert the map to an array of [target, ContextualHelp.DictionarySource[]]
        return Array.from(visibleTooltips.entries())
    }
}

const mapStateToProps = (state: GlobalState): StateProps => ({
    ...state.contextualHelp,
    helpUrl: state.userInfo.helpUrl,
})

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ContextualHelpButton)
